import { useState, useEffect } from 'react';
import { Star, Sparkles, Home, Building, Leaf, Palette, Lightbulb, Clock, CheckCircle, ArrowRight, Play, Calendar, Trophy, Zap, Phone, Layers, PaintBucket, Ruler, Camera, Settings, Heart, Users, Target, Shield, Wrench, Truck } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { useLocation, Link } from 'react-router-dom';

const ServicesPage = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [scrollY, setScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState({});
  const { pathname } = useLocation();
  
  useEffect(() => {
    // Scroll au haut de la page à chaque changement d'URL
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100,
      });
    };

    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Intersection Observer pour les animations d'apparition
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(prev => ({
            ...prev,
            [entry.target.id]: entry.isIntersecting
          }));
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const mainServices = [
    {
      icon: Home,
      title: "Décoration Intérieure",
      subtitle: "Transformez votre maison en un cocon de bien-être",
      description: "Création d'espaces intérieurs harmonieux et fonctionnels qui reflètent parfaitement votre personnalité et votre style de vie. Nous prenons en compte chaque détail pour créer un environnement unique.",
      color: "from-blue-500 to-cyan-500",
      features: [
        "Conception personnalisée selon vos goûts",
        "Optimisation intelligente de l'espace",
        "Sélection minutieuse des matériaux",
        "Coordination couleurs et textures",
        "Éclairage d'ambiance sur mesure",
        "Suivi complet du projet"
      ],
      process: [
        "Consultation et analyse des besoins",
        "Création de moodboards et concepts",
        "Plans 3D et visualisations",
        "Sélection des éléments décoratifs",
        "Installation et mise en place",
        "Finitions et ajustements"
      ]
    },
    {
      icon: Building,
      title: "Design Commercial",
      subtitle: "Renforcez votre image de marque",
      description: "Conception d'espaces commerciaux attractifs et fonctionnels qui captivent vos clients et renforcent votre identité de marque. Chaque projet est pensé pour optimiser l'expérience client.",
      color: "from-amber-500 to-orange-500",
      features: [
        "Étude de votre identité visuelle",
        "Design ergonomique et fonctionnel",
        "Éclairage professionnel adapté",
        "Mobilier sur mesure et moderne",
        "Signalétique intégrée",
        "Respect des normes commerciales"
      ],
      process: [
        "Audit de l'espace existant",
        "Analyse de la clientèle cible",
        "Conception du parcours client",
        "Design et aménagement",
        "Installation technique",
        "Formation et accompagnement"
      ]
    },
    {
      icon: Leaf,
      title: "Aménagement Extérieur",
      subtitle: "Créez votre oasis personnelle",
      description: "Transformation de vos espaces extérieurs en véritables havres de paix où il fait bon vivre. Terrasses, jardins, patios - nous donnons vie à vos rêves d'extérieur.",
      color: "from-green-500 to-emerald-500",
      features: [
        "Paysagisme créatif et durable",
        "Design de terrasses et patios",
        "Systèmes d'éclairage extérieur",
        "Mobilier outdoor résistant",
        "Installations d'eau et fontaines",
        "Espaces de détente sur mesure"
      ],
      process: [
        "Analyse du terrain et exposition",
        "Conception paysagère",
        "Sélection des végétaux",
        "Aménagement des espaces",
        "Installation des équipements",
        "Entretien et conseils"
      ]
    }
  ];

  const additionalServices = [
    {
      icon: Palette,
      title: "Conseil en Couleurs",
      description: "Harmonies chromatiques personnalisées pour vos espaces",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: Lightbulb,
      title: "Éclairage Design",
      description: "Solutions d'éclairage créatives et éco-responsables",
      color: "from-yellow-500 to-amber-500"
    },
    {
      icon: Layers,
      title: "Rénovation Complète",
      description: "Transformation totale de vos espaces",
      color: "from-indigo-500 to-purple-500"
    },
    {
      icon: PaintBucket,
      title: "Peinture Décorative",
      description: "Techniques de peinture artistique et moderne",
      color: "from-rose-500 to-red-500"
    },
    {
      icon: Ruler,
      title: "Mobilier Sur Mesure",
      description: "Création de mobilier unique adapté à vos besoins",
      color: "from-teal-500 to-cyan-500"
    },
    {
      icon: Camera,
      title: "Visualisation 3D",
      description: "Modélisation 3D pour visualiser vos projets",
      color: "from-orange-500 to-red-500"
    },
    {
      icon: Layers, // ou une icône plus appropriée
      title: "Placoplâtre Professionnel",
      description: "Solutions de cloisonnement et décoration en plaques de plâtre",
      color: "from-gray-500 to-blue-500"
    }
  ];

  const processSteps = [
    {
      icon: Users,
      title: "Consultation",
      description: "Rencontre pour comprendre vos besoins et envies"
    },
    {
      icon: Target,
      title: "Conception",
      description: "Élaboration de concepts créatifs sur mesure"
    },
    {
      icon: Settings,
      title: "Planification",
      description: "Organisation détaillée du projet et timeline"
    },
    {
      icon: Wrench,
      title: "Réalisation",
      description: "Mise en œuvre avec nos artisans qualifiés"
    },
    {
      icon: Shield,
      title: "Garantie",
      description: "Suivi post-projet et garantie qualité"
    }
  ];

  const guarantees = [
    {
      icon: Clock,
      title: "Délais Respectés",
      description: "Livraison dans les temps convenus"
    },
    {
      icon: Trophy,
      title: "Qualité Premium",
      description: "Matériaux et finitions haut de gamme"
    },
    {
      icon: Heart,
      title: "Satisfaction Client",
      description: "100% de clients satisfaits depuis 2016"
    },
    {
      icon: Truck,
      title: "Service Complet",
      description: "De la conception à l'installation"
    }
  ];

  return (
    <>
        <Helmet>
          <title>Nos Services - Placof Décor | Faux Plafonds & Décoration Intérieure</title>
          <meta 
            name="description" 
            content="Découvrez nos services en décoration intérieure, faux plafonds, placoplâtre et design mural sur mesure à Sfax." 
          />
          <meta name="keywords" content="faux plafond Sfax, installation placoplâtre, décoration murale Tunisie" />
          
          {/* Open Graph */}
          <meta property="og:title" content="Nos Services - Placof Décor | Faux Plafonds & Décoration Intérieure" />
          <meta property="og:description" content="Services professionnels en décoration intérieure et faux plafonds à Sfax." />
        
          <link rel="canonical" href={`https://www.placof-decor.tn${pathname}`} />

          <script type="application/ld+json">
            {`
              {
                "@context": "https://schema.org",
                "@type": "Service",
                "serviceType": "Installation de faux plafonds",
                "provider": {
                  "@type": "LocalBusiness",
                  "name": "Placof Décor"
                }
              }
            `}
          </script>
        </Helmet>
        
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-amber-50 overflow-hidden">
        {/* Particules de fond */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
            {[...Array(40)].map((_, i) => (
            <div
                key={i}
                className="absolute w-1 h-1 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full opacity-20 animate-pulse"
                style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${i * 0.2}s`,
                transform: `translate(${mousePosition.x * 0.01}px, ${mousePosition.y * 0.005}px)`
                }}
            />
            ))}
        </div>

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8">
            <div className="container mx-auto text-center relative z-10">
            <div
                className="space-y-8"
                style={{
                transform: `translateY(${scrollY * 0.1}px)`
                }}
            >
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-100 to-orange-100 px-6 py-3 rounded-full border border-amber-200/50 shadow-lg">
                <Sparkles className="w-5 h-5 text-amber-600 animate-pulse" />
                <span className="text-amber-700 font-semibold">Nos Services</span>
                </div>

                <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 bg-clip-text text-transparent leading-tight">
                Expertise &
                <span className="block text-4xl sm:text-5xl lg:text-6xl mt-2">
                    Créativité
                </span>
                </h1>

                <p className="text-xl sm:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                Des solutions complètes de décoration pour transformer vos espaces en 
                <span className="bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent font-semibold"> lieux d'exception</span> qui vous ressemblent.
                </p>

                {/* Boutons CTA */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <button className="group relative flex items-center gap-3 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white px-8 py-4 rounded-2xl font-bold shadow-2xl hover:shadow-amber-500/25 transition-all duration-500 overflow-hidden transform hover:scale-105">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                    <Calendar className="w-5 h-5 relative z-10 group-hover:animate-bounce" />
                    <span className="relative z-10">Consultation Gratuite</span>
                </button>

                <button className="group flex items-center gap-3 bg-white/80 backdrop-blur-sm text-gray-700 px-8 py-4 rounded-2xl font-semibold shadow-xl hover:shadow-2xl transition-all duration-500 border border-amber-100/50 hover:border-amber-200">
                    <Play className="w-5 h-5 group-hover:animate-bounce" />
                    <Link to="/projects"> <span>Voir Nos Réalisations</span> </Link>
                    <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                </button>
                </div>
            </div>
            </div>

            {/* Élément décoratif */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-full blur-3xl -z-10" />
        </section>

        {/* Services Principaux */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
            <div className="container mx-auto">
            <div className="text-center mb-16">
                <h2 className="text-4xl sm:text-5xl font-black bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 bg-clip-text text-transparent mb-6">
                Nos Services Principaux
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Trois domaines d'expertise pour concrétiser tous vos projets déco
                </p>
            </div>

            <div className="space-y-16">
                {mainServices.map((service, index) => (
                <div
                    key={index}
                    id={`main-service-${index}`}
                    data-animate
                    className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                    index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                    } ${
                    isVisible[`main-service-${index}`] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
                    style={{
                    transitionDelay: `${index * 0.2}s`
                    }}
                >
                    {/* Contenu textuel */}
                    <div className={`space-y-8 ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                    <div className="space-y-4">
                        <div className={`inline-flex items-center gap-3 bg-gradient-to-r ${service.color} p-4 rounded-2xl shadow-xl`}>
                        <service.icon className="w-8 h-8 text-white" />
                        </div>
                        
                        <h3 className={`text-3xl sm:text-2xl font-black bg-gradient-to-r ${service.color} bg-clip-text text-transparent`}>
                        {service.title}
                        </h3>
                        
                        <p className="text-lg text-gray-500 font-semibold">
                        {service.subtitle}
                        </p>
                        
                        <p className="text-gray-600 leading-relaxed text-lg">
                        {service.description}
                        </p>
                    </div>

                    {/* Fonctionnalités */}
                    <div className="space-y-4">
                        <h4 className="text-xl font-bold text-gray-800">Ce que nous proposons :</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {service.features.map((feature, featureIndex) => (
                            <div key={featureIndex} className="flex items-center gap-3">
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                            <span className="text-gray-600">{feature}</span>
                            </div>
                        ))}
                        </div>
                    </div>

                    <button className={`group flex items-center gap-3 bg-gradient-to-r ${service.color} text-white px-6 py-3 rounded-xl font-semibold shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:scale-105`}>
                        <span>En savoir plus</span>
                        <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                    </button>
                    </div>

                    {/* Carte visuelle */}
                    <div className={`relative ${index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                    <div className="group relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-2xl hover:shadow-3xl transition-all duration-500 border border-amber-100/50 transform hover:scale-105">
                        {/* Processus */}
                        <div className="space-y-6">
                        <h4 className="text-xl font-bold text-gray-800 mb-6">Notre Processus :</h4>
                        {service.process.map((step, stepIndex) => (
                            <div key={stepIndex} className="flex items-center gap-4 group/step">
                            <div className={`w-8 h-8 bg-gradient-to-r ${service.color} rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 group-hover/step:rotate-12 transition-transform duration-300`}>
                                {stepIndex + 1}
                            </div>
                            <span className="text-gray-600 group-hover/step:text-gray-800 transition-colors">
                                {step}
                            </span>
                            </div>
                        ))}
                        </div>

                        {/* Effet de brillance */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000 rounded-3xl" />
                    </div>

                    {/* Élément décoratif */}
                    <div className={`absolute -top-4 -right-4 w-16 h-16 bg-gradient-to-r ${service.color} rounded-2xl shadow-2xl flex items-center justify-center transform rotate-12`}>
                        <Star className="w-8 h-8 text-white animate-pulse" />
                    </div>
                    </div>
                </div>
                ))}
            </div>
            </div>
        </section>

        {/* Services Additionnels */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-amber-50/50 to-orange-50/50">
            <div className="container mx-auto">
            <div className="text-center mb-16">
                <h2 className="text-4xl sm:text-5xl font-black bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 bg-clip-text text-transparent mb-6">
                Services Complémentaires
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Des prestations spécialisées pour parfaire vos projets
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {additionalServices.map((service, index) => (
                <div
                    key={index}
                    id={`additional-service-${index}`}
                    data-animate
                    className={`group relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 border border-amber-100/50 text-center transform hover:scale-105 ${
                    isVisible[`additional-service-${index}`] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
                    style={{
                    transitionDelay: `${index * 0.1}s`
                    }}
                >
                    <div className={`w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-r ${service.color} flex items-center justify-center group-hover:rotate-12 transition-transform duration-300`}>
                    <service.icon className="w-8 h-8 text-white group-hover:animate-bounce" />
                    </div>
                    
                    <h3 className={`text-xl font-bold mb-4 bg-gradient-to-r ${service.color} bg-clip-text text-transparent`}>
                    {service.title}
                    </h3>
                    
                    <p className="text-gray-600 leading-relaxed">
                    {service.description}
                    </p>

                    {/* Effet de brillance */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000 rounded-3xl" />
                </div>
                ))}
            </div>
            </div>
        </section>
        {/* Placoplâtre  */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-black bg-gradient-to-r from-gray-600 to-blue-600 bg-clip-text text-transparent mb-6">
                Expertise en Placoplâtre
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Solutions modernes de cloisonnement et décoration intérieure
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  title: "Cloisons Standards",
                  description: "Pour séparer les espaces simplement et efficacement",
                  icon: Layers
                },
                {
                  title: "Cloisons Acoustiques",
                  description: "Réduction du bruit pour plus de confort",
                  icon: Shield
                },
                {
                  title: "Plafonds Suspendus",
                  description: "Design moderne et fonctionnel",
                  icon: Home
                },
                {
                  title: "Décoration Sur Mesure",
                  description: "Formes et designs personnalisés",
                  icon: Palette
                }
              ].map((item, index) => (
                <div key={index} className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-gray-100/50">
                  <div className="w-16 h-16 mb-6 rounded-2xl bg-gradient-to-r from-gray-500 to-blue-500 flex items-center justify-center">
                    <item.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 bg-gradient-to-r from-gray-600 to-blue-600 bg-clip-text text-transparent">
                    {item.title}
                  </h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>

            <div className="mt-16 bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-gray-100/50">
              <h3 className="text-2xl font-bold mb-4 bg-gradient-to-r from-gray-600 to-blue-600 bg-clip-text text-transparent">
                Avantages du Placoplâtre
              </h3>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  "Léger et facile à installer",
                  "Excellente isolation thermique et acoustique",
                  "Surface lisse prête à peindre",
                  "Flexibilité de conception",
                  "Résistance au feu",
                  "Économique et durable"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
        {/* Notre Processus */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
            <div className="container mx-auto">
            <div className="text-center mb-16">
                <h2 className="text-4xl sm:text-5xl font-black bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 bg-clip-text text-transparent mb-6">
                Comment Nous Travaillons
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Un processus éprouvé pour garantir la réussite de votre projet
                </p>
            </div>

            <div className="relative">
                {/* Ligne de progression */}
                <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full transform -translate-y-1/2" />

                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8">
                {processSteps.map((step, index) => (
                    <div
                    key={index}
                    id={`process-${index}`}
                    data-animate
                    className={`relative text-center ${
                        isVisible[`process-${index}`] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
                    style={{
                        transitionDelay: `${index * 0.2}s`
                    }}
                    >
                    <div className="group relative bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-xl hover:shadow-2xl transition-all duration-500 border border-amber-100/50 transform hover:scale-105">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-600 flex items-center justify-center group-hover:rotate-12 transition-transform duration-300">
                        <step.icon className="w-8 h-8 text-white group-hover:animate-bounce" />
                        </div>
                        
                        <h3 className="text-lg font-bold mb-2 bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                        {step.title}
                        </h3>
                        
                        <p className="text-gray-600 text-sm leading-relaxed">
                        {step.description}
                        </p>

                        {/* Numéro d'étape */}
                        <div className="absolute -top-3 -right-3 w-8 h-8 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                        {index + 1}
                        </div>

                        {/* Effet de brillance */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000 rounded-3xl" />
                    </div>
                    </div>
                ))}
                </div>
            </div>
            </div>
        </section>

        {/* Nos Garanties */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-amber-50/50 to-orange-50/50">
            <div className="container mx-auto">
            <div className="text-center mb-16">
                <h2 className="text-4xl sm:text-5xl font-black bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 bg-clip-text text-transparent mb-6">
                Nos Engagements
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Des garanties solides pour votre tranquillité d'esprit
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {guarantees.map((guarantee, index) => (
                <div
                    key={index}
                    id={`guarantee-${index}`}
                    data-animate
                    className={`group relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 border border-amber-100/50 text-center transform hover:scale-105 ${
                    isVisible[`guarantee-${index}`] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
                    style={{
                    transitionDelay: `${index * 0.1}s`
                    }}
                >
                    <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-600 flex items-center justify-center group-hover:rotate-12 transition-transform duration-300">
                    <guarantee.icon className="w-8 h-8 text-white group-hover:animate-bounce" />
                    </div>
                    
                    <h3 className="text-xl font-bold mb-4 bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                    {guarantee.title}
                    </h3>
                    
                    <p className="text-gray-600 leading-relaxed">
                    {guarantee.description}
                    </p>

                    {/* Effet de brillance */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000 rounded-3xl" />
                </div>
                ))}
            </div>
            </div>
        </section>

        {/* CTA Final */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
            <div className="container mx-auto text-center">
            <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 rounded-3xl p-12 shadow-2xl relative overflow-hidden">
                {/* Effet de brillance */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transform -translate-x-full animate-pulse" />
                
                <div className="relative z-10 space-y-8">
                <div className="flex justify-center mb-6">
                    <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-3xl flex items-center justify-center">
                    <Zap className="w-10 h-10 text-white animate-pulse" />
                    </div>
                </div>
                
                <h2 className="text-4xl sm:text-5xl font-black text-white mb-6">
                    Commençons Votre Projet Ensemble
                </h2>
                
                <p className="text-xl text-white/90 max-w-2xl mx-auto mb-8">
                    Profitez de notre consultation gratuite pour découvrir comment nous pouvons
                    transformer vos espaces selon vos rêves les plus ambitieux.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <button className="group relative flex items-center gap-3 bg-white text-amber-600 px-8 py-4 rounded-2xl font-bold shadow-2xl hover:shadow-white/25 transition-all duration-500 overflow-hidden transform hover:scale-105">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                    <Calendar className="w-5 h-5 relative z-10 group-hover:animate-bounce" />
                    <span className="relative z-10">Consultation Gratuite</span>
                    </button>

                    <a 
                        href="tel:+21697286007" 
                        className="group flex items-center gap-3 bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-2xl font-semibold shadow-xl hover:shadow-2xl transition-all duration-500 border border-white/20 hover:border-white/40"
                    >
                        <Phone className="w-5 h-5 group-hover:animate-bounce" />
                        <span>+216 97 286 007</span>
                        <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                    </a>
                </div>
                </div>
            </div>
            </div>
        </section>
        </div>
     </>
  );
};

export default ServicesPage;